import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddressPanel from 'components/AddressPanel/AddressPanel';
import Button from 'components/Button/Button';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DetailSection from 'components/DetailSection/DetailSection';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import ProfitCenterLinksCustomers from 'components/ProfitCenterLinksCustomers/ProfitCenterLinksCustomers';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import { NACE_CODE, NACE_SECTION, VALUE_CHAIN_LIST } from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import useValueChainPosition from 'hooks/useValueChainPosition';
import i18n from 'i18n';
import GeneralCustomerInfo from 'pages/UserPages/CreateCustomer/components/GeneralCustomerInfo';
import useCreateCustomerForm from 'pages/UserPages/CreateCustomer/hooks/useCreateCustomerForm';
import {
  useEnumQuery, useGetNaceCodeTreeQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums, selectNaceCodes } from 'store/slices/dataSets/selectors';
import { useCustomerMACreateMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import getRequestDataForCustomer from 'utils/formHelpers/getRequestDataForCustomer';

import type { TDate } from 'components/DatePicker/interfaces/TDate';
import type { Path, PathValue } from 'react-hook-form';
import type ICustomerRecordRequest from 'store/slices/dataSets/interfaces/ICustomerRecordRequest';
import type { TCreateCustomerSchema } from 'utils/validators';
import type ICreateCustomerModal from './interfaces/ICreateCustomerModal';

import styles from './scss/CreateCustomerModal.module.scss';

function CreateCustomerModal({
  open, title, onClose, vcmId,
}: ICreateCustomerModal) {
  const { t } = useTranslation();
  const { recordId = '' } = useParams();
  const placeholder = recordId ? t('common.notProvided') : '';

  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const [customerCreate, { isLoading: isSupplierCreateLoading }] = useCustomerMACreateMutation();

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.ownership, DATA_SETS_ENUM_TYPES.valueChainSection,
      DATA_SETS_ENUM_TYPES.businessUnitType, DATA_SETS_ENUM_TYPES.assetCategory, DATA_SETS_ENUM_TYPES.ownedLeased,
      DATA_SETS_ENUM_TYPES.upstreamTier, DATA_SETS_ENUM_TYPES.priorityRating],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const { setFieldsErrors } = useFieldsErrors();

  const isPageLoading = isEnumLoading || isNaceCodesTreeLoading || isSupplierCreateLoading;

  const CustomerEndpoint = customerCreate;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
    watch,
    setValue,
    profitCenterIds,
  } = useCreateCustomerForm({ isPageLoading });

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onCreateCustomerSubmit = handleSubmit((values) => {
    const requestData: ICustomerRecordRequest = getRequestDataForCustomer({ values, companyCurrency });

    CustomerEndpoint({
      data: requestData,
      maId: recordId,
      vcmId,
    }).unwrap()
      .then(
        () => {
          toast.success(i18n.t(
            recordId ? 'notifications.recordHasBeenUpdated'
              : 'notifications.recordHasBeenAdded',
          ));
          reset(undefined, { keepValues: true });
          onClose();
        },
      ).catch((errors) => {
        setFieldsErrors<TCreateCustomerSchema>({ errors, setError, fieldsName });
      });
  });

  const naceDivisions = watch(NACE_SECTION)?.divisions || [];

  const onNaceSelectionChange = () => {
    const emptyOption = null as PathValue<TCreateCustomerSchema, Path<TCreateCustomerSchema>>;
    setValue(NACE_CODE, emptyOption);
  };

  const {
    onFindCountries, onFindStates, onFindCities, isStatesDisabled, onChangeCountry, onChangeState,
    isCitiesDisabled, defaultCountries,
  } = useGeoLocations<TCreateCustomerSchema>({ watch, setValue });

  const { dateOpened, onDateOpenedChange } = useDataSetsDate<TCreateCustomerSchema>({ watch, setValue });

  const onChangeDate = (date: TDate, onChange: (date: TDate) => void) => {
    onChange(date);
    onDateOpenedChange(date);
  };

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root}>
        <div className={styles.formWrapper}>
          <div className={styles.wrapper}>
            <Loader isPageLoading={isPageLoading} isFullLoader={false} />
            <GeneralCustomerInfo
              control={control}
              enumData={enumData}
              placeholder={placeholder}
              dateOpened={dateOpened}
              onChangeDate={onChangeDate}
              isPageLoading={isPageLoading}
              handleGetVcOptions={handleGetVcOptions}
              vcOptionsList={vcOptionsList}
              listPosition={listPosition}
              setValue={setValue}
            />
            <DetailSection
              control={control}
              naceCodesData={naceCodesData}
              naceDivisions={naceDivisions}
              onNaceSelectionChange={onNaceSelectionChange}
              placeholder={placeholder}
              recordId={recordId}
            />
            <DataSetsPanel title={t('customers.selections.financials')} className={styles.fullWidth}>
              <ProfitCenterLinksCustomers
                control={control}
                profitCenterIds={profitCenterIds}
                placeholder={placeholder}
                watch={watch}
                setValue={setValue}
              />
            </DataSetsPanel>
            <AddressPanel
              control={control}
              placeholder={placeholder}
              onFindCountries={onFindCountries}
              onFindStates={onFindStates}
              onFindCities={onFindCities}
              isStatesDisabled={isStatesDisabled}
              isCitiesDisabled={isCitiesDisabled}
              onChangeCountry={onChangeCountry}
              onChangeState={onChangeState}
              menuPosition="absolute"
              defaultCountries={defaultCountries}
            />
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onClose}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateCustomerSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateCustomerModal;
